import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckIcon } from "lucide-react";
import { leadTimeline, pipelineStats, type TimelineEvent } from "../data/landing";
import { REVEAL_EASE } from "./Reveal";

/** Seconds after page load when the first timeline item appears. */
const FEED_START = 0.3;
const FEED_INTERVAL = 0.25;
type LeadResponsePanelProps = {
  /** "compact" = mobile version: timeline only, smaller type, no glow. */
  variant?: "full" | "compact";
};
export function LeadResponsePanel({
  variant = "full"
}: LeadResponsePanelProps) {
  const reduce = useReducedMotion();
  const compact = variant === "compact";
  return <figure className="relative" data-id="element-271" data-mp="29ezzcz18lzcd">
      {!compact && <div className="absolute -inset-10 opacity-70 blur-3xl" style={{
      background: "radial-gradient(50% 45% at 55% 45%, rgba(255,183,0,0.14), rgba(255,183,0,0) 70%)"
    }} aria-hidden="true" data-id="element-272" data-mp="2ecjc1uiae427" />}
      <div className={`relative overflow-hidden rounded-md border border-lineStrong ${compact ? "bg-ink" : "bg-surface/90 shadow-2xl shadow-black/60 backdrop-blur-xl"}`} data-id="element-273" data-mp="hz087u8a6tgx">
        <div className={`flex items-center justify-between border-b border-line bg-surface2/40 ${compact ? "px-4 py-3" : "px-5 py-4"}`} data-id="element-274" data-mp="8x0p8wdphfbc">
          <div data-id="element-275" data-mp="3v4s02fx3ai4i">
            <p className="font-ui text-sm font-bold text-cream" data-id="element-276" data-mp="3v5a1dqylzmja">Lead Response</p>
            <p className="mt-0.5 text-xs text-muted" data-id="element-277" data-mp="3qejxki5tzrqp">Pipeline · Hoy</p>
          </div>
          <span className="flex items-center gap-2 font-ui text-[11px] font-bold uppercase tracking-[0.16em] text-muted" data-id="element-278" data-mp="3mcq0ejuuh5va">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" aria-hidden="true" data-id="element-279" data-mp="3he5959xklwiz" />
            En vivo
          </span>
        </div>

        {!compact && <dl className="grid grid-cols-2 border-b border-line" data-id="element-280" data-mp="3cgmwp9kcgz68">
            {pipelineStats.map((label, i) => <div key={label} className={`px-5 py-4 ${i % 2 === 1 ? "border-l border-line" : ""} ${i >= 2 ? "border-t border-line" : ""}`} data-id="element-281" data-mp="2xrtng95iisrn" data-mpi={i}>
                <dt className="truncate font-ui text-[10px] font-bold uppercase tracking-[0.14em] text-muted" data-id="element-282" data-mp="2ornhr9g22x31">
                  {label}
                </dt>
                <dd className="mt-1.5 font-display text-2xl text-cream" data-id="element-283" data-mp="2sutki73ym64r">—</dd>
              </div>)}
          </dl>}

        <ol className={compact ? "px-4 py-5" : "px-5 py-6"} data-id="element-284" data-mp="3lhxpic31ykgj">
          {leadTimeline.map((event, i) => {
          const isLast = i === leadTimeline.length - 1;
          const motionProps = reduce ? {} : compact ? {
            initial: {
              opacity: 0,
              y: 8
            },
            whileInView: {
              opacity: 1,
              y: 0
            },
            viewport: {
              once: true,
              amount: 0.6
            },
            transition: {
              duration: 0.4,
              ease: REVEAL_EASE,
              delay: i * FEED_INTERVAL
            }
          } : {
            initial: {
              opacity: 0,
              y: 8
            },
            animate: {
              opacity: 1,
              y: 0
            },
            transition: {
              duration: 0.4,
              ease: REVEAL_EASE,
              delay: FEED_START + i * FEED_INTERVAL
            }
          };
          return <motion.li key={event.label} className={`relative pl-9 ${isLast ? "" : compact ? "pb-4" : "pb-6"}`} {...motionProps} data-id="element-285" data-mp="19u2qgwpkdyj1" data-mpi={i}>
                <TimelineRow event={event} isLast={isLast} compact={compact} data-id="element-286" data-mp="1js8np10feghk" />
              </motion.li>;
        })}
        </ol>
      </div>
      {!compact && <figcaption className="relative mt-4 text-[14px] leading-snug text-muted" data-id="element-287" data-mp="1eruhloidxgm6">
          Ejemplo de cómo se controla un lead desde que entra hasta que agenda o se pierde.
        </figcaption>}
    </figure>;
}
function TimelineRow({
  event,
  isLast,
  compact
}: {
  event: TimelineEvent;
  isLast: boolean;
  compact: boolean;
}) {
  return <>
      {!isLast && <span className="absolute bottom-0 left-[7px] top-5 w-px bg-line" aria-hidden="true" data-id="element-288" data-mp="14qk048w51tx3" />}
      <Marker tone={event.tone} data-id="element-289" data-mp="18qkejtj8m545" />
      <div className={`rounded-[3px] ${event.tone === "alert" ? "-mx-3 -my-2 border border-gold/30 bg-gold/[0.07] px-3 py-2" : ""}`} data-id="element-290" data-mp="1dqyjh2pjkyxy">
        <p className={`leading-snug text-cream ${compact ? "text-[13px]" : "text-sm"} ${event.tone === "success" ? "font-medium" : ""}`} data-id="element-291" data-mp="1inytfopiiluc">
          {event.label}
        </p>
        {event.detail && <p className={`mt-0.5 tabular-nums ${compact ? "text-[11px]" : "text-xs"} ${event.tone === "alert" ? "text-gold" : "text-muted"}`} data-id="element-292" data-mp="1whzifqqnwuhi">
            {event.detail}
          </p>}
      </div>
    </>;
}
function Marker({
  tone
}: {
  tone: TimelineEvent["tone"];
}) {
  if (tone === "success") {
    return <span className="absolute left-0 top-0.5 flex h-[15px] w-[15px] items-center justify-center rounded-full bg-gold" data-id="element-293" data-mp="2bkhgl2tqu6vq">
        <CheckIcon className="h-2.5 w-2.5 text-ink" strokeWidth={3.5} aria-hidden="true" data-id="element-294" data-mp="26kv26q26iklc" />
      </span>;
  }
  return <span className={`absolute left-0 top-0.5 flex h-[15px] w-[15px] items-center justify-center rounded-full border bg-surface ${tone === "alert" ? "border-gold" : "border-steel"}`} aria-hidden="true" data-id="element-295" data-mp="22kuppayn4jgx">
      <span className={`h-[5px] w-[5px] rounded-full ${tone === "alert" ? "bg-gold" : "bg-muted"}`} data-id="element-296" data-mp="2ud25k96bnru9" />
    </span>;
}